<template>
  <b-overlay :show="loading" class="animated">
    <modal-trash-basket
      v-if="show_modal"
      :entity_type="entity_type"
      :trash="trash"
      @onUpdate="onUpdate"
    />
    <v-server-table
      :columns="table_config.columns"
      :options="table_config.options"
      :ref="ref"
      :theme="theme"
      id="staff-server-table"
      class="w-100"
    >
      <div v-if="!work_group_id" slot="beforeTable" class="pt-3">
        <select-filter
          variant="info"
          :icon="work_group.icon"
          placeholder="All Work Groups"
          :collection="work_group.filters"
          value-key="id"
          text-key="name"
          filter-field="work_groups.id"
          filter-operator="eq"
          @onSelect="setCustomFilter('work_group', $event)"
        />
      </div>
      <span slot="last_name" slot-scope="{ row }">
        <a :href="`#/staff/${row.id}`" class="btn-link">
          {{ `${row.last_name}, ${row.first_name}` }}
        </a>
      </span>
      <span slot="work_groups.name" slot-scope="{ row }">
        {{ formatWorkGroupString(row) }}
      </span>
      <span slot="actions" slot-scope="{ row }">
        <a
          v-show="checkPermission('edit staff')"
          class="btn btn-warning px-1 py-0 text-white mr-1"
          :href="`#/staff/${row.id}/edit`"
        >
          <i class="fa fa-pencil" />
        </a>
        <template v-if="checkPermission('delete staff')">
          <a
            v-if="isDeleted(row.id)"
            class="btn btn-success px-1 py-0 text-white mr-1"
            @click="restoreFromTrash(row.id)"
          >
            <i class="fa fa-refresh" />
          </a>
          <a
            v-else
            class="btn btn-danger px-1 py-0 text-white mr-1"
            @click="putToTrash(row)"
          >
            <i class="fa fa-trash" />
          </a>
        </template>
      </span>
    </v-server-table>
  </b-overlay>
</template>

<script>
import Vue from "vue";
import BaseServerTable from "@/components/shared/BaseServerTable";
import ModalTrashBasket from "@/components/shared/ModalTrashBasket";
import SelectFilter from "@/components/shared/SelectFilter";
import StaffCollection from "@/collections/lab/StaffCollection";
import WorkGroupCollection from "@/collections/lab/WorkGroupCollection";
import WorkGroupModel from "@/models/lab/WorkGroupModel";
import { mapActions, mapGetters } from "vuex";
import { ServerTable } from "vue-tables-2";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";

Vue.use(ServerTable);

const WorkGroupRepository = LabRepositoryFactory.get("workGroup");

export default {
  name: "StaffServerTable",
  extends: BaseServerTable,
  components: {
    ModalTrashBasket,
    ServerTable,
    SelectFilter,
  },
  props: {
    collection: {
      type: Object,
      default: () => new StaffCollection(),
    },
    work_groups: {
      type: Object,
      default: () => null,
    },
    show_columns: {
      type: Array,
      default: () => ["last_name", "email", "work_groups.name", "actions"],
    },
    filterable: {
      type: Array,
      default: () => ["searchable"]
    },
    sortable: {
      type: Array,
      default: () => ["last_name", "email"],
    },
  },
  data() {
    const actions_access =
      this.checkPermission("edit staff") ||
      this.checkPermission("delete staff");

    return {
      entity_type: "staff",
      work_group_collection: null,
      work_group_id: this.$route.params.work_group_id || null,
      laboratory_id: this.$route.params.laboratory_id || null,
      table_config: Object.assign(this.getDefaultTableConfig(actions_access), {
        options: {
          headings: {
            last_name: "Name",
            email: "Email",
            "work_groups.name": "Work Groups",
            actions: "Actions",
          },
        },
      }),
    };
  },
  computed: {
    ...mapGetters({
      trash: "staffTrash"
    }),
    work_group() {
      return {
        icon: `fa-${WorkGroupModel.iconStatic()}`,
        filters: this.work_groups || this.work_group_collection,
      };
    },
    show_modal() {
      return this.checkPermission('delete staff') && this.show_columns.includes("actions")
    }
  },
  methods: {
    ...mapActions(["PUT_TO_TRASH", "RESTORE_FROM_TRASH"]),
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    prepareCollectionFilter(filter) {
      this.laboratory_id &&
        filter.filterEquals("laboratories.id", this.laboratory_id);
      this.work_group_id && 
        filter.filterIn("workGroups.id", this.work_group_id)

      return filter;
    },
    isDeleted(id) {
      return this.trash.find(item => item.id === id);
    },
    putToTrash(value) {
      this.PUT_TO_TRASH({ value, name: this.entity_type });
    },
    restoreFromTrash(id) {
      this.RESTORE_FROM_TRASH({ id, name: this.entity_type });
    },
    onUpdate() {
      this.$refs[this.ref].refresh();
    },
    async init() {
      if (!this.work_groups) {
        const { data } = await WorkGroupRepository.getAll();

        this.work_group_collection = data.data.reduce((acc, cur) => {
          acc.add(cur);

          return acc
        }, new WorkGroupCollection())
      }
    },
    formatWorkGroupString(staff) {
      return staff.work_groups.length
        ? staff.work_groups.map(({ name }) => name).join(", ")
        : null;
    }
  },
  created() {
    this.init();
  },
};
</script>
